import { useFieldConfig } from 'context/fields';
import groupBy from 'lodash/groupBy';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { FormProvider, useForm } from 'react-hook-form';
import { AddlFieldType } from 'shared/constants/fields';
import { arrayInsertAt, noop } from 'shared/utils/misc';
import AsyncSelect from './AsyncSelect';
import Attachments from './Attachments';
import Checkbox from './Checkbox';
import Date from './Date';
import Group from './Group';
import Hyperlink from './Hyperlink';
import Text from './Input';
import Label from './Label';
import Number from './Number';
import RichText from './RichText';
import Select from './Select';
import Tag from './Tag';
import Textarea from './Textarea';

export const fieldTypeToComponentMap = {
  [AddlFieldType.STR]: Text,
  [AddlFieldType.NUM]: Number,
  [AddlFieldType.TEXT]: Textarea,
  [AddlFieldType.BOOL]: Checkbox,
  [AddlFieldType.HYPERLINK]: Hyperlink,
  [AddlFieldType.DATE]: Date,
  [AddlFieldType.DATETIME]: Date,
  [AddlFieldType.SELECT]: Select,
  [AddlFieldType.SELECT_MULTI]: Select,
  [AddlFieldType.OBJECT_RELATION]: AsyncSelect,
  [AddlFieldType.OBJECT_RELATION_MULTI]: AsyncSelect,
  [AddlFieldType.RICH_TEXT]: RichText,
  [AddlFieldType.TAG]: Tag,
};

export default function FormBase({
  id,
  fields,
  entity,
  entityType,
  children,
  hasNoFields,
  defaultValues,
  useGlobal = false,
  includeAttachmentArea,
  fieldsToShow,
  fieldsToMerge,
  emptyMessage,
  isSelective,
  onSubmit = noop,
  onChange = noop,
  onReady = noop,
  onCanSubmit = noop,
}) {
  const { showBoundary } = useErrorBoundary();
  const [disabled, setDisabled] = useState(false);
  const [attachments, setAttachments] = useState([]);
  // Tracks custom validation errors from custom form components
  // that don't affect RFH's `isValid` state, e.g. <MultiInput>.
  const [hasCustomError, setCustomError] = useState(false);
  const { processEntity, getAttachmentEntities } = useFieldConfig();
  const methods = useForm({ defaultValues, disabled });
  const {
    formState,
    control,
    register,
    subscribe,
    clearErrors,
    setError,
    setValue,
    trigger,
    handleSubmit: rhfHandleSubmit,
  } = methods;
  const isEditing = !isEmpty(entity);

  const includeAttachment = useMemo(
    () =>
      isNil(includeAttachmentArea)
        ? getAttachmentEntities({ useGlobal }).includes(entityType)
        : includeAttachmentArea,
    [includeAttachmentArea, useGlobal, entityType]
  );
  const groupedFields = useMemo(() => {
    if (isEmpty(fields)) return [];
    const { null: ungroupedFields = [], ...fieldsByGroupName } = groupBy(
      fields,
      'portalGroupName'
    );
    return Object.keys(fieldsByGroupName).reduce((acc, groupName) => {
      const fieldsToInsert = fieldsByGroupName[groupName];
      const idx = fields.findIndex(
        (field) => field.portalGroupName === groupName
      );
      return arrayInsertAt(acc, idx, fieldsToInsert);
    }, ungroupedFields);
  }, [fields]);

  /**
   * useForm()'s default `mode: onSubmit` will not immediately validate on mount
   * unless triggered manually. We need this to validate default values.
   * Stick to `onSubmit` mode since the other options will impact
   * performanceon on large forms.
   */
  useEffect(() => {
    trigger();
  }, []);

  useEffect(() => {
    const unsubscribe = subscribe({
      formState: {
        values: true,
        errors: true,
        defaultValues: true,
        touchedFields: true,
        dirtyFields: true,
        isReady: true,
        isValid: true,
        isDirty: true,
        isValidating: true,
        isSubmitting: true,
        isSubmitSuccessful: true,
      },
      callback: (formState) =>
        onChange({
          ...formState,
          data: processEntity({
            useGlobal,
            entityType,
            entity: formState.values,
            mode: 'toServer',
            fieldsToShow,
            fieldsToMerge,
          }),
        }),
    });
    return () => unsubscribe();
  }, [subscribe]);

  useEffect(() => {
    const { isReady } = formState;
    if (isReady) onReady();
  }, [formState, onReady]);

  useEffect(() => {
    const { isReady, isValid, isValidating, isSubmitting } = formState;
    const canSubmit =
      isReady && isValid && !isValidating && !isSubmitting && !hasCustomError;
    onCanSubmit(canSubmit);
  }, [formState, onCanSubmit, hasCustomError]);

  const appendAttachments = useCallback(
    () => includeAttachment && { attachment_ids: attachments.map((a) => a.id) },
    [attachments, includeAttachment]
  );

  const renderField = useCallback(
    ({
      fieldType,
      displayName,
      portalOrder: order,
      portalReadOnly: readOnlyBase,
      portalReadOnlyEdit: readOnlyEdit,
      portalReadOnlyCreate: readOnlyCreate,
      portalRequired: required,
      portalDefaultValue: defaultValue,
      additionalAttribute: isAdditional = false,
      ...config
    }) => {
      const readOnly =
        (isEditing ? readOnlyEdit : readOnlyCreate) ?? readOnlyBase;
      const FormComponent = fieldTypeToComponentMap[fieldType];
      const fieldConfig = {
        ...config,
        order,
        required,
        readOnly,
        fieldType,
        displayName,
        defaultValue,
        isAdditional,
      };
      const setFormError = (message = '') => {
        if (message) {
          setCustomError(true);
          setError(config.name, { type: 'custom', message });
        } else {
          setCustomError(false);
          clearErrors(config.name);
        }
      };

      if (!FormComponent) return null;
      return (
        <fieldset key={`${fieldType}_${config.name}`}>
          <Label
            htmlFor={config.name}
            fieldType={fieldType}
            required={required}
          >
            {displayName}
          </Label>
          <FormComponent
            control={control}
            register={register}
            setError={setFormError}
            entityType={entityType}
            setValue={setValue}
            isSelective={isSelective}
            {...fieldConfig}
          />
        </fieldset>
      );
    },
    [entityType]
  );

  const renderFieldOrGroup = useCallback(
    (fieldOrGroup) => {
      const isGroup = Array.isArray(fieldOrGroup);
      if (isGroup) {
        const fieldGroup = fieldOrGroup;
        const groupName = fieldGroup.at(0)?.portalGroupName;
        return (
          <Group key={groupName} name={groupName}>
            {fieldGroup.map(renderField)}
          </Group>
        );
      } else {
        const field = fieldOrGroup;
        return renderField(field);
      }
    },
    [renderField]
  );

  const submitHandler = useCallback(
    async (data) => {
      try {
        setDisabled(true);
        await onSubmit(
          // Processed payload
          {
            ...processEntity({
              useGlobal,
              entityType,
              entity: data,
              mode: 'toServer',
            }),
            ...appendAttachments(),
          },
          // Unprocessed payload
          { ...data, ...appendAttachments() }
        );
      } catch (e) {
        if (!e.isAxiosError) showBoundary(e);
      } finally {
        setDisabled(false);
      }
    },
    [onSubmit, appendAttachments, entityType, useGlobal]
  );

  const interceptAndSubmit = useCallback(
    (event) => {
      // Intercept before handing off to react-hook-form
      // to prevent submits from inner form from bubbling up to parent,
      // e.g., Project form submission auto-submitting Order form by mistake.
      event?.preventDefault?.();
      event?.stopPropagation?.();
      return rhfHandleSubmit(submitHandler)(event);
    },
    [submitHandler]
  );

  return (
    <>
      {hasNoFields && !includeAttachment && (
        <div className="d-grid place-items-center w-100 h-100">
          {emptyMessage || 'No input needed. Please continue to the next step.'}
        </div>
      )}
      <FormProvider {...methods}>
        <div className={disabled ? 'opacity-50 pe-none' : 'opacity-100'}>
          {children}
          <form id={id} onSubmit={interceptAndSubmit}>
            {groupedFields.map(renderFieldOrGroup)}
          </form>
          {includeAttachment && (
            <Attachments
              entity={entity}
              entityType={entityType}
              onUploaded={setAttachments}
            />
          )}
        </div>
      </FormProvider>
    </>
  );
}
